import {
  Paper,
  TextField,
  Typography
} from "@material-ui/core";
import clsx from "clsx";
import React, { useEffect, useState, useMemo } from "react";
import { t } from "ttag";
import Header from "../../components/Header/Header";
import Loading from "../../components/Loading/Loading";
import ServiceNavigationBar from "../../components/Menu/ServiceNavigationBar";
import { Button, FilterRequire, Spinner } from '@coolremote/shared';
import { useStoreActions, useStoreState } from "../../models/RootStore";
import AddEditGroupsPopup from "./AddEditGroupsPopup";
import ClickableRow from "./ClickableRow";
import useStyles from "./Groups.style";
import Row from "./Row";
import useChurnZero from "@hooks/useChurnZero";
import { Search } from "@icons/index";
import { useSiteUniversalUnitsForGroups } from "@hooks/useSiteGroupableUniversalUnits";
import { getGroupMemberIds } from "@utils/universalUnit";

const Groups: React.FC = (props: any) => {
  const classes = useStyles();

  const getSiteGroups = useStoreActions((a) => a.groups.getSiteGroups);
  const deleteGroup = useStoreActions((a) => a.groups.deleteGroupAPI);
  const updateGroup = useStoreActions((a) => a.groups.updateGroupAPI);
  const createGroup = useStoreActions((a) => a.groups.createGroup);
  const addGroup = useStoreActions((a) => a.groups.addGroup);
  const { addMessage } = useStoreActions((a) => a.errorMessage);

  const isInitialized = useStoreState((s) => s.isInitialized);
  const types = useStoreState((s) => s.types);
  const allUnits = useStoreState((s) => s.units.allUnits);
  const sites = useStoreState((s) => s.sites.allSites);
  const allSensors = useStoreState((s) => s.sensors.allSensors);
  const { selections } = useStoreState((s) => s.selections);
  const { customerId, siteId = "" } = selections;
  const getCustomer = useStoreState((state) => state.customers.getCustomer);
  const { canAddGroup = true } = getCustomer(customerId) || {};

  const [groups, setGroups] = useState<any>({});
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [editIdGroup, setEditIdGroup] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { groupableUnits: universalUnits, isLoading: universalUnitsLoading } = useSiteUniversalUnitsForGroups(siteId);

  const units = useMemo(() => {
    const result = { ...allUnits };
    Object.values(result).forEach((unit: any) => {
      // type 1 = indoor; origin 1 = hvac. BACnet/Modbus units are added from universalUnits below, by their raw ids
      if (unit.type !== 1 || unit.origin !== 1) {
        delete result[unit.id];
      }
    });
    return { ...result, ...universalUnits };
  }, [allUnits, universalUnits]);

  const sensors = useMemo(() => {
    const { sensorTypes } = types;
    const result: any = {};
    Object.values(allSensors).forEach((sensor: any) => {
      if (sensorTypes[sensor.type]?.enableView) {
        result[sensor.id] = sensor;
      }
    });
    return result;
  }, [allSensors, types]);

  const site = sites[siteId || ""];

  const {trackEvent} = useChurnZero()

  useEffect(() => {
    setGroups({});
    setSelectedGroup("");
    if (!siteId) {
      return;
    }
    getSiteGroups(siteId)
      .then((fetchedGroups: any) => setGroups(fetchedGroups))
      .catch((err: any) => addMessage({ message: err.message }));
  }, [siteId]);

  const groupsForDisplay = useMemo(() => {
    const result: any = {};
    Object.values(groups).forEach((group: any) => {
      const { sensors: groupSensors, id } = group;
      result[id] = {
        ...group,
        units: getGroupMemberIds(group).filter((unitId: string) => units[unitId]),
        sensors: (groupSensors ?? []).filter((sensorId: string) => sensors[sensorId]),
      };
    });
    return result;
  }, [groups, units, sensors]);

  const deleteSelectedGroup = (id: string) => {
    deleteGroup(id).then(() => {
      setGroups((prev: any) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }).catch((err: any) => {
      addMessage({ message: err.message });
    });
  };

  const updateEditedGroup = (groupId: string, data: any) => {
    return updateGroup({ groupId, data }).then((group: any) => {
      setGroups((prev: any) => ({ ...prev, [group.id]: group }));
    })
      .catch((err: any) => addMessage({ message: err.message }))
      .finally(() => setEditIdGroup(""));
  };

  const createNewGroup = (data: any) => {
    return createGroup(data).then((group: any) => {
      setGroups((prev: any) => ({ ...prev, [group.id]: group }));
      addGroup(group);
    })
      .catch((err: any) => addMessage({ message: err.message }))
      .finally(() => setEditIdGroup(""));
  };

  const handleGroupSelect = (id: any) => {
    setSelectedGroup(selectedGroup === id ? "" : id);
  };

  const handleAddNewClick = () => {
    trackEvent('GroupsAddGroup','The user initiated a group setup');
    setEditIdGroup("new")
  };

  if (!isInitialized) {
    return <Loading />;
  }

  const filteredGroups = Object.values(groupsForDisplay).filter((group: any) => {
    if (!searchTerm) {
      return true;
    }
    const term = searchTerm.toLowerCase();
    if (group.name?.toLowerCase().includes(term)) {
      return true;
    }
    if (group.units?.some((unitId: string) => units[unitId]?.name?.toLowerCase().includes(term))) {
      return true;
    }
    if (group.sensors?.some((sensorId: string) => sensors[sensorId]?.name?.toLowerCase().includes(term))) {
      return true;
    }
    return false;
  });

  const isSelectedVisible = filteredGroups.some((g: any) => g.id === selectedGroup);
  const groupObj = (isSelectedVisible ? groupsForDisplay[selectedGroup] : null) || {};
  const { id: groupId = "", units: groupUnits = [], sensors: groupSensors = [] } = groupObj;

  return (
    <div className={classes.view}>
      <ServiceNavigationBar {...props} />
      <div className={classes.contentArea}>
        <Header
          hideSystemSelection
          hideUnitSelection
          path={[t`Settings - Groups`]}
          screenTitle="groups"
          customGeneralNames={{ site: t`Select Site` }}
        />
        {!siteId ? <FilterRequire type={t`site`} /> :
          <>
            <div className={classes.btnContainer}>
              <Button
                className={classes.newbtn}
                disabled={!canAddGroup || !site?.permissions?.canCreateGroups}
                onClick={handleAddNewClick}>
                  {t`Add New`}
                </Button>
            </div>
            <Paper elevation={0} className={classes.paperTableContainer}>
              <div className={classes.groupsSection}>
                <div className={classes.titleRow}>
                  <Typography className={classes.title}>{t`Groups`}</Typography>
                </div>
                <div className={classes.searchWrapper}>
                  <TextField
                    variant="outlined"
                    size="small"
                    className={classes.searchInput}
                    placeholder={t`Search for Groups or Units names`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <Search />
                    }}
                  />
                </div>
                <div className={clsx(classes.itemsContainer, classes.groupsListContainer)}>
                  {filteredGroups.length > 0 ? (
                    filteredGroups.map((group: any, index: number) => {
                      const { canUpdate, canDelete } = group?.permissions || {};
                      return <ClickableRow
                        key={index}
                        name={group.name}
                        selected={group.id === groupId}
                        unitsNumber={universalUnitsLoading ? <Spinner size={16} /> : group?.units?.length + group?.sensors?.length}
                        onDelete={() => deleteSelectedGroup(group.id)}
                        onEdit={(event: any) => {
                          event.stopPropagation();
                          event.preventDefault();
                          setEditIdGroup(group.id);
                        }}
                        onClick={() => handleGroupSelect(group.id)}
                        canEdit={canUpdate && !universalUnitsLoading}
                        canDelete={canDelete}
                      />;
                    })
                  ) : (
                    <Typography className={classes.noResultsText}>
                      {t`No results found. Try searching for a different group or unit name`}
                    </Typography>
                  )}
                </div>
              </div>
              <div className={classes.unitssSection}>
                <Typography className={classes.title}>{t`Units`}</Typography>
                <div className={clsx(classes.itemsContainer, classes.unitsItemsContainer)}>
                  {groupId ?
                    <>
                      <ClickableRow
                        key={siteId}
                        name={site?.name}
                        selected={true}
                        unitsNumber={universalUnitsLoading ? <Spinner size={16} /> : groupUnits.length + groupSensors.length}
                        collapse={true}
                        removeMargin={true}
                        noIcon
                      />
                      {!universalUnitsLoading && groupUnits.map((unitId: string, index: number) =>
                        <Row key={`${groupId}-${unitId}`} name={units[unitId]?.name} hideLine={(groupUnits.length - 1 === index) && groupSensors.length === 0} />
                      )}
                      {!universalUnitsLoading && groupSensors.map((sensorId: any, index: number) =>
                        <Row key={`${groupId}-${sensorId}`} name={sensors[sensorId]?.name} hideLine={groupSensors.length - 1 === index} />
                      )}
                    </>
                 : <Typography className={classes.noSelectionText}>{t`Please select a group from the list at the left`}</Typography> }
                </div>
              </div>
            </Paper>
          </>
        }
      </div>
      {editIdGroup &&
        <AddEditGroupsPopup
          onClose={() => setEditIdGroup("")}
          group={groups[editIdGroup]}
          sites={sites}
          units={units}
          sensors={sensors}
          unitTypes={types?.unitTypes}
          createGroup={createNewGroup}
          updateGroup={updateEditedGroup}
          site={site}
        />
      }
    </div >
  );
};

export default Groups;
